import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Target, Pencil, Check, X } from 'lucide-react';

const Stat = ({ label, value, testId, accent }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`text-sm font-semibold tabular-nums ${accent || ''}`} data-testid={testId}>{value}</span>
  </div>
);

const Block = ({ title, children, testId }) => (
  <Card className="card-shadow" data-testid={testId}>
    <CardContent className="p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">{title}</p>
      {children}
    </CardContent>
  </Card>
);

const GoalRow = ({ goal, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(goal);
  if (!editing) {
    return (
      <div className="flex items-center justify-between py-1.5 border-b border-border/60">
        <span className="text-sm text-muted-foreground flex items-center gap-1"><Target className="w-3.5 h-3.5" />Enrollment Goal</span>
        <span className="flex items-center gap-1">
          <span className="text-sm font-semibold tabular-nums" data-testid="rt-enrollment-goal">{goal}</span>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setValue(goal); setEditing(true); }} data-testid="rt-edit-goal-btn"><Pencil className="w-3 h-3" /></Button>
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 py-1">
      <Input type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} className="h-7" data-testid="rt-goal-input" />
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={async () => { await onSave(Number(value) || 0); setEditing(false); }} data-testid="rt-save-goal-btn"><Check className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(false)} data-testid="rt-cancel-goal-btn"><X className="w-4 h-4" /></Button>
    </div>
  );
};

const OutreachBlock = ({ title, data, n }) => (
  <Block title={title} testId={`rt-contact${n}-metrics`}>
    <Stat label="Total Enrollments" value={data.enrolled} testId={`rt-c${n}-enrolled`} accent="text-green-700" />
    <Stat label="Total Declines" value={data.declined} testId={`rt-c${n}-declined`} />
    <Stat label="Total FNS Not-Eligible" value={data.fns_not_eligible} testId={`rt-c${n}-fns`} />
    <Stat label="Total No Contacts" value={data.no_contacts} testId={`rt-c${n}-no-contacts`} />
    <Stat label="Total Pending Contacts" value={data.pending_contacts} testId={`rt-c${n}-pending`} />
  </Block>
);

export const ReferralSummary = ({ summary, onSaveGoal }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4" data-testid="rt-summary">
    <Block title="Enrollment Metrics" testId="rt-enrollment-metrics">
      <GoalRow goal={summary.enrollment_goal} onSave={onSaveGoal} />
      <Stat label="Total Enrollments" value={summary.total_enrollments} testId="rt-total-enrollments" accent="text-green-700" />
      <Stat label="Remaining Enrollments" value={summary.remaining_enrollments} testId="rt-remaining-enrollments" />
    </Block>
    <Block title="Referral Metrics" testId="rt-referral-metrics">
      <Stat label="Total Referrals" value={summary.total_referrals} testId="rt-total-referrals" />
      <Stat label="Direct Referrals" value={summary.direct_referrals} testId="rt-direct-referrals" />
      <Stat label="Reverse Referrals" value={summary.reverse_referrals} testId="rt-reverse-referrals" />
      <Stat label="Direct - Forsyth County" value={summary.forsyth_referrals} testId="rt-forsyth-referrals" />
    </Block>
    <Block title="Initial Outreach Metrics" testId="rt-initial-metrics">
      {Object.entries(summary.initial_outreach).map(([s, v]) => (
        <Stat key={s} label={s} value={v} testId={`rt-initial-${s.toLowerCase().replace(/[^a-z]+/g, '-')}`} />
      ))}
    </Block>
    <OutreachBlock title="Contact 2" data={summary.contact_2} n={2} />
    <OutreachBlock title="Contact 3" data={summary.contact_3} n={3} />
    <OutreachBlock title="Contact 4" data={summary.contact_4} n={4} />
  </div>
);
